import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Copy, Mic, Pencil, Plus, Sparkles, Square, Trash2 } from 'lucide-react';
import CreateSurveyModal from './CreateSurveyModal';
import LimitExceededModal from './LimitExceededModal';
import Checkbox from '../../common/Checkbox';
import FormField from '../../common/FormField';
import Loader from '../../common/Loader';
import Modal from '../../common/Modal';
import SectionEyebrow from '../../common/SectionEyebrow';
import { useLanguage } from '../../../assets/js/LanguageContext';
import { sendPinEmails } from '../../../utils/pinEmails';
import '../../../assets/styles/ChooseSurvey.css';
import '../../../assets/styles/TemplateBuilder.css';
import '../../../assets/styles/SurveyAnswer.css';
import { clampRating } from '../../common-questions/TRating';

const QuestionGenerator = ({ onBack }) => {
  const { t, language } = useLanguage();
  const [topic, setTopic] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState(null); // For displaying errors to the user

  // State for audio recording
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // State for question type selection
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState([]);

  // New state for modal visibility
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [generatedPin, setGeneratedPin] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const buildShareMessage = () => `${t('share_greeting')}

${t('share_link_intro')}

thepul.se

${t('share_code_intro')}

${generatedPin}

${t('share_long_text_hint')}

${t('share_thanks')}`;

  const navigate = useNavigate();

  // Base URL for your Flask API - Corrected to 3002
  const API_BASE_URL = process.env.REACT_APP_AI_URL;

  // Rotating titles array
  const rotatingTitles = [
    t('prompt_title_working'),
    t('prompt_title_mind'),
    t('prompt_title_discover'),
    t('prompt_title_research'),
    t('prompt_title_explore')
  ];

  const changingPlaceholders = [
    t('prompt_placeholder_survey'),
    t('prompt_placeholder_topic'),
    t('prompt_placeholder_area'),
    t('prompt_placeholder_project'),
    t('prompt_placeholder_aspect')
  ];

  // Rotate titles every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTitleIndex((prevIndex) => (prevIndex + 1) % rotatingTitles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Rotate placeholders every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prevIndex) => (prevIndex + 1) % changingPlaceholders.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Parses an AI-generated question string into a structured question object.
   * Determines question type (ranking, closed, open) based on patterns.
   * @param {string} questionString - The raw question string from the AI.
   * @param {number} index - Index for unique ID generation.
   * @returns {object} A structured question object.
   */
  const parseAIQuestion = (questionString, index) => {
    // Trim the question string once at the beginning to clean up whitespace
    questionString = questionString.trim();

    // Helper to ensure question text ends with a question mark
    const ensureQuestionMark = (text) => (text.endsWith('?') ? text : text + '?');

    // 1. Attempt to parse Ranking Question (Numeric Scale)
    // This regex looks for phrases like "del X al Y", "de X a Y", "entre X y Y", or "X-Y"
    // It is designed to be more robust and less likely to be mistaken for closed.
    // It captures the question text (group 1), min value (group 2), and max value (group 3).
    const rankingRegex = /(.*?)?\s*(?:en una escala\s*|on a scale\s*)?(?:del|de|entre|from|\s+)?\s*(\d+)\s*(?:al|-|a|y|to)\s*(\d+)(?:[^a-zA-Z0-9,;:]*)$/i;
    let rankingMatch = questionString.match(rankingRegex);

    if (rankingMatch) {
      let qText = rankingMatch[1] ? rankingMatch[1].trim() : ''; // Get the text before the scale
      const minVal = parseInt(rankingMatch[2], 10);
      const maxVal = parseInt(rankingMatch[3], 10);

      // Clean up common prefixes from qText that might be redundant with ranking context
      qText = qText.replace(/en una escala$/, '').trim();
      qText = qText.replace(/valora$/, '').trim();
      qText = qText.replace(/qué tan$/, '').trim();
      qText = qText.replace(/en qué medida$/, '').trim();
      qText = qText.replace(/califica$/, '').trim(); // Add 'califica'
      qText = qText.replace(/on a scale$/i, '').trim();

      // Ensure valid range and minVal is actually less than maxVal
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal < maxVal) {
        return {
          id: `ranking-${Date.now()}-${index}`, // Unique ID for keying
          text: questionString, // Keep full raw text for selection display
          type: 'rating',
          question: ensureQuestionMark(qText || questionString.split(rankingRegex)[0].trim()), // Fallback to part of original string
          minValue: clampRating(Math.min(minVal, maxVal), Math.max(minVal, maxVal)).min,
          maxValue: clampRating(Math.min(minVal, maxVal), Math.max(minVal, maxVal)).max,
          selected: false, // Default to not selected
        };
      }
    }

    // 2. Attempt to parse Closed Question: "Tu género? (Masculino, Femenino, No binario)"
    // This must come AFTER ranking match to prioritize ranking if there's any ambiguity.
    // Looks for options enclosed in parentheses `()` or square brackets `[]` at the end of the question.
    let closedMatch = questionString.match(/(.+)\s*\(([^)]+)\)$|(.+)\s*\[([^\]]+)\]$/);
    if (closedMatch) {
        const qText = (closedMatch[1] || closedMatch[3]).trim();
        const optionsStr = (closedMatch[2] || closedMatch[4]).trim();
        let options;

        // Prioritize splitting by comma, then semicolon, then slash.
        // Filter out empty strings that might result from extra delimiters.
        if (optionsStr.includes(',')) {
            options = optionsStr.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0);
        } else if (optionsStr.includes(';')) {
            options = optionsStr.split(';').map(opt => opt.trim()).filter(opt => opt.length > 0);
        } else if (optionsStr.includes('/')) {
            options = optionsStr.split('/').map(opt => opt.trim()).filter(opt => opt.length > 0);
        } else {
            // If no common delimiters, and it's not a ranking (already checked), treat as single option
            // but be cautious if it looks like a range (e.g., "1-5" without being caught by ranking)
            if (!/^\d+\s*-\s*\d+$/.test(optionsStr)) { // Avoid single option like "1-5" here
                 options = [optionsStr];
            } else {
                 options = []; // Treat as no valid options, fall through to open
            }
        }

        // Only return as closed question if valid options were found
        if (options.length > 0) {
            return {
                id: `closed-${Date.now()}-${index}`,
                text: questionString,
                type: 'closed',
                question: ensureQuestionMark(qText),
                options: options,
                selected: false,
            };
        }
    }

    // 3. Default to Open Question if no specific pattern for Closed or Ranking matches.
    return {
      id: `open-${Date.now()}-${index}`,
      text: questionString,
      type: 'open',
      question: ensureQuestionMark(questionString),
      selected: false,
    };
  };

  // --- Audio Recording Functions ---
  const startRecording = async () => {
    setError(null); // Clear any previous errors
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      // Detectar el mejor formato de audio compatible
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/wav';
          }
        }
      }
      
      console.log('Formato de audio seleccionado:', mimeType);
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = []; // Clear previous audio chunks

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Chunk de audio recibido, tamaño:', event.data.size);
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('Grabación detenida, chunks recolectados:', audioChunksRef.current.length);
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log("Grabación detenida, subiendo para transcribir... Tamaño del blob:", audioBlob.size);
        await uploadAudioForTranscription(audioBlob, mimeType);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // Start recording with 1 second timeslice for better chunk handling
      mediaRecorderRef.current = mediaRecorder; // Store recorder instance
      setIsRecording(true);
      console.log("Grabación iniciada...");
    } catch (err) {
      console.error("Error al iniciar la grabación:", err);
      setError(t('audio_recording_start_error'));
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop(); // Stop recording
      setIsRecording(false);
      console.log("Deteniendo grabación...");
    }
  };

  const uploadAudioForTranscription = async (audioBlob, mimeType) => {
    // Mostrar mensaje de transcripción
    setIsTranscribing(true);
    setError(null); // Clear previous errors

    const formData = new FormData();
    
    // Determinar la extensión correcta basada en el MIME type
    let extension = 'webm';
    if (mimeType.includes('mp4')) extension = 'm4a';
    else if (mimeType.includes('wav')) extension = 'wav';
    
    const filename = `recording_${Date.now()}.${extension}`;
    formData.append('file', audioBlob, filename);
    formData.append('lang', language);
    
    console.log('Subiendo archivo:', filename, 'Tipo MIME:', mimeType, 'Tamaño:', audioBlob.size);

    try {
      const response = await fetch(`${API_BASE_URL}/upload-audio`, {
        method: 'POST',
        body: formData
      });

      console.log('Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(`Error en la transcripción: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('Datos recibidos del servidor:', data);
      
      if (data.transcription && data.transcription.trim()) {
        // Add new transcription after existing text
        setTopic(prevTopic => {
          const newText = data.transcription.trim();
          if (prevTopic.trim()) {
            return `${prevTopic} ${newText}`;
          } else {
            return newText;
          }
        });
      } else {
        setError(t('audio_not_detected_text'));
      }
      } catch (err) {
        console.error('Error al subir archivo de audio para transcribir:', err);
        // Check if it's a "no transcription" error
        if (err.message.includes("No se recibió transcripción") || err.message.includes("transcription")) {
          setError(t('audio_not_detected_text'));
        } else {
          setError(t('audio_transcription_error'));
        }
      } finally {
        setIsTranscribing(false);
      }
  };

  // --- Handler for selecting question types (checkboxes) ---
  const handleQuestionTypeChange = (type) => {
    setSelectedQuestionTypes(prevTypes =>
      prevTypes.includes(type)
        ? prevTypes.filter(t => t !== type) // Remove if already selected
        : [...prevTypes, type] // Add if not selected
    );
  };

  // --- Function to generate suggested questions from AI ---
  const handleGenerateQuestions = async (isAdditional = false) => {
    // 1. Frontend validation: Topic required
    if (!topic.trim()) {
      setError(t('qg_error_topic_required'));
      return;
    }

    // 2. Frontend validation: Question type selection required for initial generation
    if (selectedQuestionTypes.length === 0 && !isAdditional) {
      setError(t('qg_error_type_required'));
      return;
    }

    setIsLoading(true); // Show loading indicator
    setError(null); // Clear any previous errors

    try {
      const payload = {
        topic: topic,
        num_questions: isAdditional ? 3 : 5, // Generate 3 for additional, 5 for initial
        // Send original text of existing questions to AI to avoid duplicates
        existing_questions: isAdditional ? suggestedQuestions.map(q => q.text) : [],
        question_types: selectedQuestionTypes, // Send selected types to backend
        lang: language
      };

      const response = await fetch(`${API_BASE_URL}/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido del servidor' }));
        throw new Error(errorData.error || response.statusText);
      }

      const data = await response.json();
      if (data.suggestions && Array.isArray(data.suggestions)) {
        // Parse each raw suggested string into a structured question object
        const newParsedQuestions = data.suggestions.map((qStr, index) =>
          parseAIQuestion(qStr, suggestedQuestions.length + index)
        );

        if (isAdditional) {
          setSuggestedQuestions(prev => [...prev, ...newParsedQuestions]); // Append new questions
        } else {
          setSuggestedQuestions(prev => [...prev, ...newParsedQuestions]); // Always append, never replace
        }
      } else {
        throw new Error("Formato de respuesta inesperado de la API.");
      }

    } catch (err) {
      console.error("Error al generar preguntas:", err);
      setError(t('qg_error_generate'));
    } finally {
      setIsLoading(false); // Hide loading indicator
    }
  };

  // --- Handler for selecting individual suggested questions (checkboxes) ---
  const handleCheckboxChange = (questionId) => {
    setSuggestedQuestions(prev =>
      prev.map(q =>
        q.id === questionId ? { ...q, selected: !q.selected } : q // Toggle selected status
      )
    );
  };

  // --- Seleccionar / deseleccionar todas las preguntas sugeridas de una vez ---
  const handleToggleSelectAll = () => {
    setSuggestedQuestions(prev => {
      const next = !(prev.length > 0 && prev.every(q => q.selected));
      return prev.map(q => ({ ...q, selected: next }));
    });
  };


  // --- Edit handlers ---
  const handleEditQuestion = (questionId, currentText) => {
    setEditingQuestionId(questionId);
    setEditingText(currentText);
  };

  const handleSaveEdit = () => {
    if (!editingText.trim()) return;

    setSuggestedQuestions(prev =>
      prev.map(q =>
        q.id === editingQuestionId 
          ? { ...q, text: editingText.trim(), question: editingText.trim() }
          : q
      )
    );
    setEditingQuestionId(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditingText('');
  };

  // --- Delete handlers ---
  const handleDeleteQuestion = (questionId) => {
    setSuggestedQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const handleDeleteOption = (questionId, optionIndex) => {
    setSuggestedQuestions(prev =>
      prev.map(q =>
        q.id === questionId && q.type === 'closed'
          ? { ...q, options: q.options.filter((_, index) => index !== optionIndex) }
          : q
      )
    );
  };


  // --- Handle "Usar Preguntas Seleccionadas" button click ---
  const handleOpenSurveyModal = () => {
    const selected = suggestedQuestions.filter(q => q.selected);
    if (selected.length === 0) {
      setError(t('qg_error_select_question'));
      return;
    }
    setError(null); // Clear any previous errors
    setShowSurveyModal(true); // Open the survey creation modal
  };

  // --- Handle survey creation submission from the modal ---
  const handleCopy = () => {
    const message = buildShareMessage();

    navigator.clipboard.writeText(message)
      .then(() => setCopySuccess(true))
      .catch(() => setCopySuccess(false));
  };

  const handleFinish = () => {
    setShowPopup(false);
    navigate('/dashboard');
  };

  const handleCreateSurveySubmit = async (surveyData) => {
    setIsLoading(true); // Show loading spinner for API call
    setError(null); // Clear previous errors
    setLimitError(null);

    // Get JWT token from localStorage (assuming it's stored here after login)
    const token = localStorage.getItem('token');
    if (!token) {
        setLimitError({ message: t('qg_error_not_authenticated') });
        setIsLoading(false);
        return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/encuestas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Attach JWT token to Authorization header
        },
        body: JSON.stringify(surveyData), // Send formatted survey data
      });

      if (!response.ok) {
        // Try to get specific error message from server
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido del servidor' }));
        
        // Limite del plan (diagnosticos al mes o participantes): aviso con los datos
        if (response.status === 403 && errorData.detalles) {
          setShowSurveyModal(false);
          setLimitInfo(errorData);
          setIsLoading(false);
          return;
        }

        throw new Error(errorData.error || response.statusText);
      }

      const data = await response.json();
      console.log("Encuesta creada con éxito:", data);
      setGeneratedPin(data.pin);
      // Aviso por email a los participantes del Excel, igual que al crear desde plantilla o desde cero
      sendPinEmails({ emails: surveyData.participantes && surveyData.participantes.emails, pin: data.pin, language });
      setShowPopup(true);
      setShowSurveyModal(false); // Close modal on successful creation
    } catch (err) {
      console.error("Error al crear la encuesta:", err);
      // Se muestra dentro del modal de crear, que sigue abierto (detras no se veria)
      setLimitError({ message: t('preview_save_error') });
    } finally {
      setIsLoading(false); // Hide loading spinner
    }
  };

  const typeOptions = [
    { key: 'open', label: t('question_type_open_plural') },
    { key: 'closed', label: t('question_type_closed_plural') },
    { key: 'rating', label: t('question_type_rating_plural') }
  ];
  const selectedCount = suggestedQuestions.filter((q) => q.selected).length;
  const allSelected = suggestedQuestions.length > 0 && selectedCount === suggestedQuestions.length;
  const typeLabel = (type) => (typeOptions.find((option) => option.key === type) || {}).label || t('question_type_none');

  return (
    <div className="tp-choose tp-builder tp-prompt">
      {onBack && (
        <button type="button" className="tp-choose__back" onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          {t('common_back')}
        </button>
      )}

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('choose_prompt')}</SectionEyebrow>
          <h1 className="tp-choose__title">{rotatingTitles[currentTitleIndex]}</h1>
          <p className="tp-choose__lead">{t('prompt_lead')}</p>
        </div>
      </header>

      <section className="tp-form tp-prompt__composer">
        <FormField
          id="prompt-topic"
          label={t('prompt_topic_label')}
          as="textarea"
          rows={4}
          value={topic}
          placeholder={changingPlaceholders[currentPlaceholderIndex]}
          disabled={isLoading || isRecording}
          onChange={(e) => setTopic(e.target.value)}
        />

        <div className="tp-prompt__types">
          <span className="tp-prompt__types-label">{t('prompt_types_label')}</span>
          <div className="tp-prompt__chips">
            {typeOptions.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`tp-prompt__chip ${selectedQuestionTypes.includes(key) ? 'is-selected' : ''}`.trim()}
                aria-pressed={selectedQuestionTypes.includes(key)}
                disabled={isLoading}
                onClick={() => handleQuestionTypeChange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="tp-form__alert" role="alert">{error}</p>}

        <div className="tp-prompt__actions">
          <button
            type="button"
            className={`tp-answer__mic ${isRecording ? 'is-recording' : ''}`.trim()}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading || isTranscribing}
            aria-pressed={isRecording}
          >
            {isRecording
              ? <Square size={13} strokeWidth={2.5} aria-hidden="true" />
              : <Mic size={16} strokeWidth={2} aria-hidden="true" />}
            {isRecording ? t('answer_dictate_stop') : t('answer_dictate')}
          </button>

          <button
            type="button"
            className="btn btn--primary"
            onClick={() => handleGenerateQuestions(false)}
            disabled={isLoading || isRecording || !topic.trim() || selectedQuestionTypes.length === 0}
          >
            <Sparkles size={16} strokeWidth={2} aria-hidden="true" />
            {t('prompt_generate')}
          </button>
        </div>

        {isRecording && (
          <p className="tp-answer__dictate-status" role="status">
            <span className="tp-answer__rec-dot" aria-hidden="true" />
            {t('answer_recording')}
          </p>
        )}
      </section>

      {suggestedQuestions.length > 0 && (
        <section className="tp-builder__panel">
          <div className="tp-builder__panel-head">
            <h2 className="tp-builder__count">
              {t('prompt_selected_count').replace('{selected}', selectedCount).replace('{total}', suggestedQuestions.length)}
            </h2>
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleToggleSelectAll}>
              {allSelected ? t('prompt_unselect_all') : t('prompt_select_all')}
            </button>
          </div>

          <ol className="tp-builder__list">
            {suggestedQuestions.map((q) => (
              <li key={q.id} className="tp-builder__item tp-prompt__item">
                <Checkbox checked={q.selected} onChange={() => handleCheckboxChange(q.id)} hideLabel>
                  {q.question}
                </Checkbox>
                <div className="tp-builder__body">
                  {editingQuestionId === q.id ? (
                    <>
                      <textarea
                        className="tp-prompt__edit"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                      />
                      <div className="tp-builder__actions">
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleSaveEdit}>
                          {t('homeqg_edit_save')}
                        </button>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={handleCancelEdit}>
                          {t('homeqg_edit_cancel')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="tp-builder__text">{q.question}</p>
                      <p className="tp-builder__meta">
                        <span className={`tp-dmod__type is-${q.type}`}>{typeLabel(q.type)}</span>
                      </p>
                      {q.type === 'closed' && (q.options || []).length > 0 && (
                        <ul className="tp-prompt__options">
                          {q.options.map((option, optionIndex) => (
                            <li key={optionIndex}>
                              {option}
                              <button
                                type="button"
                                className="tp-prompt__option-remove"
                                onClick={() => handleDeleteOption(q.id, optionIndex)}
                                aria-label={t('question_delete_option')}
                              >
                                <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
                {editingQuestionId !== q.id && (
                  <div className="tp-builder__actions">
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => handleEditQuestion(q.id, q.question)}
                    >
                      <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                      {t('homeqg_edit_modify')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm tp-builder__delete"
                      onClick={() => handleDeleteQuestion(q.id)}
                    >
                      <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                      {t('homeqg_delete')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ol>

          <div className="tp-builder__panel-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => handleGenerateQuestions(true)}
              disabled={isLoading || !topic.trim()}
            >
              <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              {t('prompt_generate_more')}
            </button>
            <button type="button" className="btn btn--primary" onClick={handleOpenSurveyModal} disabled={selectedCount === 0}>
              {t('prompt_use_selected')}
              <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </section>
      )}

      {showSurveyModal && (
        <CreateSurveyModal
          onClose={() => {
            setShowSurveyModal(false);
            setLimitError(null);
          }}
          onSubmit={handleCreateSurveySubmit}
          selectedQuestions={suggestedQuestions.filter((q) => q.selected)}
          initialTopic={topic}
          limitError={limitError}
        />
      )}

      <LimitExceededModal
        isOpen={Boolean(limitInfo)}
        onClose={() => setLimitInfo(null)}
        limitInfo={limitInfo}
      />

      {(isLoading || isTranscribing) && (
        <Loader overlay caption={isTranscribing ? t('prompt_transcribing_audio') : t('prompt_generating_questions')} />
      )}

      <Modal
        open={showPopup}
        onOpenChange={(open) => { if (!open) handleFinish(); }}
        variant="confirm"
        title={t('preview_pin_title')}
        description={t('preview_pin_text')}
        confirmText={t('common_finish')}
        onConfirm={handleFinish}
        size="md"
      >
        <div className="tp-preview__pin">
          <span className="tp-preview__pin-label">{t('dash_th_pin')}</span>
          <strong className="tp-preview__pin-value">{generatedPin}</strong>
        </div>
        <p className="tp-preview__share-label">{t('template_share_suggested_text')}</p>
        <textarea className="tp-preview__share" readOnly rows={10} value={buildShareMessage()} />
        <button type="button" className="btn btn--outline tp-preview__copy" onClick={handleCopy}>
          {copySuccess
            ? <Check size={15} strokeWidth={2.5} aria-hidden="true" />
            : <Copy size={15} strokeWidth={2} aria-hidden="true" />}
          {copySuccess ? t('common_copied') : t('common_copy_text')}
        </button>
      </Modal>
    </div>
  );
};

export default QuestionGenerator;
